/**
 * Help Content Registry - Centralized help content management
 *
 * Provides a centralized registry for all help content in the application.
 * This is where help content is defined and registered with the HelpContentProvider.
 */

import HelpContentProvider from './HelpContentProvider';

// ============================================================================
// HELP CONTENT DEFINITIONS
// ============================================================================

/**
 * Help content for SmartSection component
 */
const SMART_SECTION_HELP = {
  'smart-section-basic': `
    <strong>SmartSection</strong> provides a clean, organized way to group related form fields and content.
    <br/><br/>
    Use it to:
    <br/>• Group related form fields
    <br/>• Organize dialog content
    <br/>• Create collapsible sections
  `,

  'smart-section-collapsible': `
    <strong>Collapsible Sections</strong> help organize large forms by allowing users to expand/collapse content.
    <br/><br/>
    Features:
    <br/>• Click the header to toggle
    <br/>• Use the arrow button for keyboard navigation
    <br/>• Maintains state across theme changes
  `,

  'smart-section-help-demo': `
    This is a <strong>demonstration</strong> of the help system integration.
    <br/><br/>
    The help system supports:
    <br/>• Rich HTML content with <em>formatting</em>
    <br/>• Theme-aware styling
    <br/>• Async content loading
    <br/>• Caching for performance
  `,

  'smart-section-performance': `
    <strong>Performance Optimization</strong> for large forms with many sections.
    <br/><br/>
    Best practices:
    <br/>• Use defaultCollapsed for non-critical sections
    <br/>• Group related fields efficiently
    <br/>• The component is optimized for 30+ sections
  `,

  'smart-section-theme': `
    <strong>Theme Integration</strong> ensures consistent styling across light and dark themes.
    <br/><br/>
    Features:
    <br/>• Dynamic color updates
    <br/>• No hardcoded values
    <br/>• Automatic hover states
    <br/>• Accessible color contrasts
  `,
};

/**
 * Help content for Dialog components
 */
const DIALOG_HELP = {
  'base-dialog-modal': `
    <strong>Modal Dialogs</strong> overlay the main content and require user interaction.
    <br/><br/>
    Features:
    <br/>• Backdrop click to close (optional)
    <br/>• ESC key to close
    <br/>• Focus management for accessibility
  `,

  'base-dialog-embedded': `
    <strong>Embedded Dialogs</strong> display inline within other content without backdrop.
    <br/><br/>
    Use cases:
    <br/>• Form sections within pages
    <br/>• Inline editing interfaces
    <br/>• Step-by-step wizards
  `,

  'dialog-header-help': `
    <strong>Dialog Headers</strong> provide title, subtitle, and action buttons.
    <br/><br/>
    Components:
    <br/>• Title and optional subtitle
    <br/>• Close button (optional)
    <br/>• Help button (like this one!)
    <br/>• Custom action buttons
  `,

  'dialog-footer-actions': `
    <strong>Dialog Footers</strong> contain primary and secondary actions.
    <br/><br/>
    Best practices:
    <br/>• Primary action on the right
    <br/>• Secondary actions on the left
    <br/>• Use loading states for async operations
  `,
};

/**
 * Help content for Form Field components
 */
const FORM_FIELD_HELP = {
  'text-field-validation': `
    <strong>Text Field Validation</strong> provides real-time feedback.
    <br/><br/>
    Features:
    <br/>• Field-level validation rules
    <br/>• Error state styling
    <br/>• Helper text for guidance
  `,

  'select-dropdown-options': `
    <strong>Select Dropdowns</strong> provide options from a predefined list.
    <br/><br/>
    Features:
    <br/>• Search functionality (optional)
    <br/>• Multiple selection support
    <br/>• Loading states for async options
  `,

  'number-input-format': `
    <strong>Number Input</strong> handles numeric values with formatting.
    <br/><br/>
    Formats:
    <br/>• Currency ($1,234.56)
    <br/>• Percentage (50.5%)
    <br/>• Plain numbers (1234)
  `,
};

/**
 * Help content for advanced features
 */
const ADVANCED_HELP = {
  'theme-system': `
    <strong>Theme System</strong> provides consistent styling across the application.
    <br/><br/>
    Features:
    <br/>• Light and dark mode support
    <br/>• Dynamic color palette
    <br/>• Typography scaling
    <br/>• No hardcoded values
  `,

  'accessibility-features': `
    <strong>Accessibility Features</strong> ensure the interface is usable by everyone.
    <br/><br/>
    Includes:
    <br/>• Keyboard navigation
    <br/>• Screen reader support
    <br/>• High contrast support
    <br/>• Focus management
  `,

  'performance-tips': `
    <strong>Performance Optimization</strong> keeps the interface responsive.
    <br/><br/>
    Techniques:
    <br/>• Component memoization
    <br/>• Lazy loading for large datasets
    <br/>• Efficient re-rendering
    <br/>• Memory leak prevention
  `,
};

// ============================================================================
// REGISTRY INITIALIZATION
// ============================================================================

/**
 * Combined help content registry
 */
export const HELP_CONTENT_REGISTRY = {
  ...SMART_SECTION_HELP,
  ...DIALOG_HELP,
  ...FORM_FIELD_HELP,
  ...ADVANCED_HELP,
};

/**
 * Initialize the help content provider with all registered content
 */
export const initializeHelpContent = (): void => {
  const provider = HelpContentProvider.getInstance();
  provider.registerBulk(HELP_CONTENT_REGISTRY);
};

/**
 * Register additional help content at runtime
 */
export const registerHelpContent = (helpId: string, content: string): void => {
  const provider = HelpContentProvider.getInstance();
  provider.registerContent(helpId, content);
};

/**
 * Register multiple help contents at runtime
 */
export const registerBulkHelpContent = (contentMap: Record<string, string>): void => {
  const provider = HelpContentProvider.getInstance();
  provider.registerBulk(contentMap);
};

/**
 * Get all available help content IDs
 */
export const getAvailableHelpIds = (): string[] => {
  return Object.keys(HELP_CONTENT_REGISTRY);
};

/**
 * Check if help content exists for a given ID
 */
export const hasHelpContent = (helpId: string): boolean => {
  return Object.prototype.hasOwnProperty.call(HELP_CONTENT_REGISTRY, helpId);
};

// Auto-initialize when module is imported
initializeHelpContent();

export default HELP_CONTENT_REGISTRY;